import { useEffect, useState } from "react";
import { Gift, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { centsToDollars } from "@/lib/referral";

interface ParentReferralCode {
  code: string;
  childName: string | null;
  sessionLabel: string;
  discountPercent: number;
  maxDiscountCents: number;
}

/**
 * "Bring a Friend & Save" — shows the codes automatically issued to this
 * family's children. Codes are generated server-side; parents never invent one.
 * Renders nothing when the session has no active referral promotion.
 */
export function ReferralCodeCard({ sessionId }: { sessionId: string | null }) {
  const [codes, setCodes] = useState<ParentReferralCode[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    supabase.functions
      .invoke("my-referral-codes", { body: { sessionId } })
      .then(({ data }) => {
        if (!cancelled && Array.isArray(data?.codes)) setCodes(data.codes);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (codes.length === 0) return null;

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* clipboard unavailable — the code is on screen anyway */
    }
  };

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 text-left space-y-3">
      <div className="flex items-center gap-2">
        <Gift className="h-5 w-5 text-primary" aria-hidden />
        <h2 className="font-heading text-lg font-bold text-foreground">Bring a Friend &amp; Save</h2>
      </div>
      {codes.map((c) => (
        <div key={c.code} className="space-y-1">
          <p className="text-sm text-muted-foreground">
            Share {c.childName ? `${c.childName}'s` : "your child's"} referral code:
          </p>
          <div className="flex items-center gap-2">
            <code className="rounded-md bg-background px-3 py-2 font-mono text-base font-semibold tracking-wide">
              {c.code}
            </code>
            <Button type="button" size="sm" variant="secondary" onClick={() => copy(c.code)}>
              {copied === c.code ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              <span className="ml-1">{copied === c.code ? "Copied" : "Copy"}</span>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            When a friend registers their child for {c.sessionLabel}, they'll receive{" "}
            {c.discountPercent}% off, up to {centsToDollars(c.maxDiscountCents)}.
          </p>
        </div>
      ))}
    </div>
  );
}
